'use client';

import { LoadingSpinner } from '@goodneighbor/ui';
import { useRouter, usePathname } from 'next/navigation';
import { useEffect } from 'react';
import styles from './authGuard.module.scss';
import useAccountQuery from '@/hooks/useAccountQuery';
import { removeJWT } from '@/services/jwt';
import globToRegexp from 'glob-to-regexp';
import { accountHasRoles } from '@goodneighbor/roles';

const partnerRoutes = ['/partner/dashboard', '/partner/dashboard/**'];

const checkIsPartner = (
  account: Awaited<ReturnType<typeof useAccountQuery>>['data']
) => {
  return (
    !!account?.data?.organization &&
    account.data.organization.status === 'ACTIVE'
  );
};

const checkHasDashboardHomeAccess = (
  account: Awaited<ReturnType<typeof useAccountQuery>>['data']
) => {
  return accountHasRoles({
    account,
    operation: 'OR',
    requiredRoles: ['PARTNER_ACCOUNT_OWNER', 'PARTNER_MANAGER']
  });
};

const isOnPartnerRoute = (pathname: string) => {
  return partnerRoutes.some((route) => {
    const regex = globToRegexp(route);
    return regex.test(pathname);
  });
};

const getRedirectPath = ({
  pathname,
  isError,
  isPartner,
  hasDashboardHomeAccess
}: {
  pathname: string;
  isError: boolean;
  isPartner: boolean;
  hasDashboardHomeAccess: boolean;
}): string | null => {
  if (isError) {
    return isOnPartnerRoute(pathname) ? '/partner' : '/volunteer';
  }
  if (pathname === '/' || (isOnPartnerRoute(pathname) && !isPartner)) {
    return '/volunteer/dashboard';
  }
  if (pathname === '/partner/dashboard' && !hasDashboardHomeAccess) {
    return '/partner/dashboard/events';
  }
  return null;
};

export default function AuthGuard({
  children
}: Readonly<{
  children?: React.ReactNode;
}>) {
  const pathname = usePathname();
  const router = useRouter();
  const account = useAccountQuery();
  const isPartner = checkIsPartner(account.data);
  const hasDashboardHomeAccess = checkHasDashboardHomeAccess(account.data);

  const redirectPath = !account.isLoading
    ? getRedirectPath({
        pathname,
        isError: account.isError,
        isPartner,
        hasDashboardHomeAccess
      })
    : null;

  useEffect(() => {
    if (account.isError) {
      removeJWT();
    }
    if (redirectPath) {
      router.replace(redirectPath);
    }
  }, [redirectPath, account.isError, router]);

  if (account.isLoading || redirectPath) {
    return (
      <div className={styles.fullPage}>
        <LoadingSpinner />
      </div>
    );
  }

  return <>{children}</>;
}
